import { useEffect, useState } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { AlertCircle, CheckCircle2, MapPin } from "lucide-react";

const SEVERITY_COLOR = {
  low: "text-emerald-700 bg-emerald-50 border-emerald-200",
  moderate: "text-amber-800 bg-amber-50 border-amber-200",
  severe: "text-red-700 bg-red-50 border-red-200",
  unknown: "text-gray-700 bg-gray-100 border-gray-200",
};

/**
 * Single photo card — fetches the binary as a blob, shows AI severity / tags,
 * EXIF GPS link to Google Maps, capture timestamp + camera model.
 */
export function PhotoCard({ photo }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let revoke;
    let cancelled = false;
    api
      .get(`/files/${photo.id}/download`, { responseType: "blob" })
      .then((r) => {
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setSrc(url);
      })
      .catch((err) => {
        // Image fetch failures degrade to the "Loading…" placeholder.
        // Log so ops sees if signed-download is failing repeatedly.
        logger.debug("[photo] blob fetch failed:", err?.response?.status);
      });
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [photo.id]);

  const ai = photo.ai_analysis || {};
  const sev = SEVERITY_COLOR[ai.severity] || SEVERITY_COLOR.unknown;

  return (
    <div
      className="rounded-xl border border-[#E5E7EB] bg-white overflow-hidden"
      data-testid={`photo-card-${photo.id}`}
    >
      <div className="aspect-[4/3] bg-gray-100">
        {src ? (
          <img
            src={src}
            alt={photo.original_filename}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">
            Loading…
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {ai.damage_detected ? (
            <span className="text-xs flex items-center gap-1 text-[#C25934]">
              <AlertCircle className="w-3.5 h-3.5" /> Damage detected
            </span>
          ) : (
            <span className="text-xs flex items-center gap-1 text-emerald-700">
              <CheckCircle2 className="w-3.5 h-3.5" /> Clear
            </span>
          )}
          <span
            className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${sev}`}
          >
            {ai.severity || "—"}
          </span>
        </div>
        {ai.summary && <p className="text-sm text-gray-700">{ai.summary}</p>}
        {ai.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3">
            {ai.tags.map((t, i) => (
              <span
                key={`${t}-${i}`}
                className="text-xs bg-[#F9F8F6] border border-[#E5E7EB] rounded-full px-2 py-0.5 text-gray-700"
              >
                {t}
              </span>
            ))}
          </div>
        )}
        {photo.exif && (photo.exif.gps || photo.exif.captured_at) && (
          <div className="mt-3 pt-3 border-t border-[#E5E7EB] text-xs text-gray-500 space-y-1">
            {photo.exif.gps && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-[#1E3F33]" />
                <a
                  href={`https://www.google.com/maps?q=${photo.exif.gps.lat},${photo.exif.gps.lng}`}
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-[#1E3F33] hover:underline"
                  data-testid={`gps-link-${photo.id}`}
                >
                  {photo.exif.gps.lat.toFixed(5)},{" "}
                  {photo.exif.gps.lng.toFixed(5)}
                </a>
              </div>
            )}
            {photo.exif.captured_at && (
              <div>📅 Captured: {photo.exif.captured_at}</div>
            )}
            {photo.exif.camera && (
              <div className="text-gray-400">{photo.exif.camera}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
